// Humanizes a PushEntryDeliveryEvents row's Meta JSON into a panel that's
// readable at a glance: a plain-English summary, supporting facts as
// labeled chips, and the raw JSON tucked into an expander for debugging.
//
// Status-specific summarizers live in `summarizers` below. Each receives
// the meta object and returns { headline, subline, facts: [{label, value}] }.

const HOUR = 3_600_000
const MIN = 60_000
const SEC = 1_000

function formatDuration(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return null
  if (ms < SEC) return `${ms}ms`
  if (ms < MIN) return `${Math.round(ms / SEC)}s`
  if (ms < HOUR) {
    const m = Math.floor(ms / MIN)
    const s = Math.round((ms % MIN) / SEC)
    return s ? `${m}m ${s}s` : `${m}m`
  }
  const h = Math.floor(ms / HOUR)
  const m = Math.round((ms % HOUR) / MIN)
  return m ? `${h}h ${m}m` : `${h}h`
}

function formatTimezone(offsetMin) {
  if (typeof offsetMin !== 'number' || !Number.isFinite(offsetMin)) return null
  // JS getTimezoneOffset is the inverse of common usage: -120 means UTC+2.
  const utcOffsetHours = -offsetMin / 60
  const sign = utcOffsetHours === 0 ? '' : utcOffsetHours > 0 ? '+' : '-'
  const abs = Math.abs(utcOffsetHours)
  const whole = Math.floor(abs)
  const frac = Math.round((abs - whole) * 60)
  return frac === 0 ? `UTC${sign}${whole}` : `UTC${sign}${whole}:${String(frac).padStart(2, '0')}`
}

function formatPlatform(platform) {
  if (typeof platform !== 'string') return null
  const lower = platform.toLowerCase()
  if (lower === 'web' || lower === 'looplly_web') return 'Web (PWA)'
  if (lower === 'ios' || lower === 'looplly_ios') return 'iOS (PWA)'
  if (lower === 'android' || lower === 'looplly_android') return 'Android (PWA)'
  if (lower.startsWith('looplly_')) return platform.slice('looplly_'.length).toUpperCase() + ' (PWA)'
  return platform
}

function formatConnection(connectionType, downlink, rtt) {
  const parts = []
  if (typeof connectionType === 'string' && connectionType) {
    parts.push(connectionType.toUpperCase())
  }
  if (typeof downlink === 'number' && Number.isFinite(downlink) && downlink > 0) {
    parts.push(`${downlink} Mbps`)
  }
  if (typeof rtt === 'number' && Number.isFinite(rtt) && rtt > 0) {
    parts.push(`${rtt}ms RTT`)
  }
  return parts.length ? parts.join(' · ') : null
}

function formatClickOutcome(outcome, openedClientUrl, targetUrl) {
  // Map machine-readable outcomes to plain-English descriptions and the
  // operational signal each one carries.
  const onTarget = openedClientUrl && targetUrl && openedClientUrl === targetUrl
  const outcomes = {
    focused_existing_exact: {
      label: 'Brought existing tab forward',
      detail: 'User already had the target page open; the SW just focused it.',
    },
    opened_window_exact: {
      label: 'Opened a new tab to the target',
      detail: 'New window opened directly on the target URL.',
    },
    opened_window_navigated: {
      label: 'Opened a new tab, then navigated to target',
      detail: 'New window opened on a different URL and was redirected to the target.',
    },
    fallback_focus_after_open_null: {
      label: 'Focused fallback tab (openWindow blocked)',
      detail: 'Browser refused to open a new window; the SW focused another existing tab.',
    },
    fallback_focus_after_open_threw: {
      label: 'Focused fallback tab (openWindow errored)',
      detail: 'openWindow threw an error; the SW focused another existing tab.',
    },
    open_null_no_clients: {
      label: 'No tabs opened',
      detail: 'openWindow returned null and no existing tabs were available.',
    },
    open_threw_no_clients: {
      label: 'Open errored, no fallback',
      detail: 'openWindow threw and no existing tabs were available to focus.',
    },
    no_action: {
      label: 'No navigation taken',
      detail: 'No target URL on the notification.',
    },
  }
  const known = outcomes[outcome]
  if (!known) return outcome ? { label: outcome, detail: null } : null
  return {
    ...known,
    onTarget,
  }
}

function formatFcmError(code, status, message) {
  const codeMap = {
    UNREGISTERED: 'Token expired or unregistered (likely safe to retire this device)',
    INVALID_ARGUMENT: 'Malformed request (caller-side bug, not the device)',
    SENDER_ID_MISMATCH: 'FCM project mismatch (config issue, alert ops)',
    QUOTA_EXCEEDED: 'FCM rate limit hit (back off)',
    UNAVAILABLE: 'FCM transient issue (retry)',
    INTERNAL: 'FCM internal error (retry)',
    THIRD_PARTY_AUTH_ERROR: 'APNS / web push auth failure',
    TRANSPORT_ERROR: 'Network failure reaching FCM (transient)',
  }
  if (typeof code === 'string' && codeMap[code]) {
    return { code, hint: codeMap[code], status, message }
  }
  return {
    code: code ?? status ?? 'Unknown',
    hint: typeof message === 'string' && message ? message : null,
    status,
    message,
  }
}

function formatTabActivity(total, visible, focused) {
  if (typeof total !== 'number') return null
  if (total === 0) return 'No looplli tabs open at click time'
  if (focused > 0) return `${total} tab${total === 1 ? '' : 's'} open · was focused`
  if (visible > 0) return `${total} tab${total === 1 ? '' : 's'} open · was visible (background)`
  return `${total} tab${total === 1 ? '' : 's'} open · was backgrounded`
}

// Per-status summarizers. Each returns:
//   { headline: string, subline?: string, facts: [{label, value}] }
const summarizers = {
  delivered(meta) {
    const ok = meta.showNotificationResult === 'success'
    const headline = ok
      ? 'Notification displayed on device'
      : 'Notification failed to display'
    const facts = []
    if (!ok && meta.showNotificationError) {
      facts.push({ label: 'Reason', value: String(meta.showNotificationError) })
    }
    if (typeof meta.activeNotificationCount === 'number') {
      facts.push({
        label: 'Active notifications',
        value: String(meta.activeNotificationCount),
      })
    }
    return { headline, facts }
  },

  clicked(meta) {
    const ttc = formatDuration(meta.timeToClickMs)
    const outcome = formatClickOutcome(
      meta.clickOutcome,
      meta.openedClientUrl,
      meta.targetUrl,
    )
    const headline = ttc
      ? `Clicked ${ttc} after delivery`
      : 'Clicked'
    const subline = outcome?.label ?? null
    const facts = []
    if (outcome?.detail) facts.push({ label: 'Outcome', value: outcome.detail })
    if (meta.targetUrl) {
      facts.push({
        label: outcome?.onTarget ? 'On target' : 'Target',
        value: meta.targetUrl,
        mono: true,
      })
    }
    if (
      meta.openedClientUrl &&
      meta.openedClientUrl !== meta.targetUrl
    ) {
      facts.push({
        label: 'Landed on',
        value: meta.openedClientUrl,
        mono: true,
      })
    }
    const tabActivity = formatTabActivity(
      meta.totalClientCount,
      meta.visibleClientCount,
      meta.focusedClientCount,
    )
    if (tabActivity) facts.push({ label: 'Tabs', value: tabActivity })
    if (meta.action) {
      facts.push({ label: 'Action button', value: String(meta.action) })
    }
    return { headline, subline, facts }
  },

  closed(meta) {
    const ttc = formatDuration(meta.timeToClickMs)
    return {
      headline: ttc ? `Dismissed ${ttc} after delivery` : 'Dismissed',
      facts: [],
    }
  },

  fanned_out(meta) {
    const platform = formatPlatform(meta.platform)
    return {
      headline: platform
        ? `Sent to FCM · ${platform}`
        : 'Sent to FCM',
      facts: [
        meta.fcm_message_name && {
          label: 'FCM message id',
          value: meta.fcm_message_name,
          mono: true,
        },
      ].filter(Boolean),
    }
  },

  fcm_rejected(meta) {
    const err = formatFcmError(meta.fcm_error_code, meta.fcm_status, meta.fcm_message)
    return {
      headline: `FCM rejected: ${err.code}`,
      subline: err.hint,
      facts: [
        meta.http_status && { label: 'HTTP status', value: String(meta.http_status) },
        meta.fcm_status && err.code !== meta.fcm_status && {
          label: 'FCM status',
          value: meta.fcm_status,
        },
        meta.fcm_message && err.code !== 'UNREGISTERED' && {
          label: 'Message',
          value: meta.fcm_message,
        },
        meta.platform && { label: 'Platform', value: formatPlatform(meta.platform) },
      ].filter(Boolean),
    }
  },

  no_targets(meta) {
    const reasonMap = {
      no_devices_tagged: 'The audience tag has no registered devices',
      tagged_devices_outside_account: 'Tagged devices belong to a different push account',
    }
    const reason = (meta.reason && reasonMap[meta.reason]) || meta.reason || 'No targets'
    return {
      headline: 'No devices reached',
      subline: reason,
      facts: [
        meta.tag && { label: 'Tag', value: String(meta.tag) },
      ].filter(Boolean),
    }
  },

  permission_revoked() {
    return {
      headline: 'Notification permission was revoked on this device',
      facts: [],
    }
  },

  failed(meta) {
    return {
      headline: 'Send failed',
      facts: [
        meta.error_message && { label: 'Error', value: meta.error_message },
        meta.error_code && { label: 'Code', value: meta.error_code },
      ].filter(Boolean),
    }
  },
}

function buildDeviceFacts(meta) {
  // Common device/network/locale facts shared across event types.
  // Only included when present — keeps the panel quiet for sparse rows.
  const facts = []
  const platform = formatPlatform(meta.platform)
  const browser = typeof meta.browser === 'string' ? meta.browser : null
  if (platform || browser) {
    facts.push({
      label: 'Device',
      value: [browser, platform].filter(Boolean).join(' on '),
    })
  }
  const locale = []
  if (typeof meta.language === 'string' && meta.language) locale.push(meta.language)
  const tz = formatTimezone(meta.timezone)
  if (tz) locale.push(tz)
  if (locale.length) facts.push({ label: 'Locale', value: locale.join(' · ') })
  const conn = formatConnection(meta.connectionType, meta.downlink, meta.rtt)
  if (conn) facts.push({ label: 'Connection', value: conn })
  return facts
}

export default function PushEventMeta({ status, meta }) {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return null
  const lower = String(status || '').toLowerCase()
  const summarize = summarizers[lower]
  const summary = summarize ? summarize(meta) : { headline: null, facts: [] }
  const deviceFacts = buildDeviceFacts(meta)
  const allFacts = [...(summary.facts ?? []), ...deviceFacts]
  const hasContent = summary.headline || summary.subline || allFacts.length > 0

  return (
    <div className="mt-2 space-y-2">
      {hasContent && (
        <div className="bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700">
          {summary.headline && (
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {summary.headline}
            </p>
          )}
          {summary.subline && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {summary.subline}
            </p>
          )}
          {allFacts.length > 0 && (
            <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {allFacts.map((fact, i) => (
                <div key={i} className="flex flex-col">
                  <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[10px]">
                    {fact.label}
                  </dt>
                  <dd
                    className={`text-gray-900 dark:text-gray-100 break-words ${
                      fact.mono ? 'font-mono text-[11px]' : ''
                    }`}
                  >
                    {fact.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
      <details>
        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
          Raw JSON
        </summary>
        <pre className="mt-1 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded overflow-x-auto">
          {JSON.stringify(meta, null, 2)}
        </pre>
      </details>
    </div>
  )
}
